import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Hls from "hls.js";

import { useAuth } from "@contexts/AuthContext";

import {
    CAPTION_LANGUAGE_NAMES,
    langsMatch,
    toCanonicalLang,
    viewerLanguage,
} from "../captionsLanguages";
import { buildMockCues } from "../mockCaptions";

// Per-session explicit choice: undefined = the viewer never touched the
// captions menu; null = chose "off"; "pt_BR"/"en"/"es" = chose that language.
// Module-level so it survives playlist navigation (the player remounts per
// content) — the default rule must never clobber an explicit choice.
let sessionChoice;

/**
 * useCaptions — subtitle track state + the default-subtitle rule.
 *
 * Playable tracks come from the HLS master (SUBTITLE_TRACKS_UPDATED), never
 * from the API: a cached master may not carry renditions yet. The API's
 * `video.captions` projection only drives the button's generating/none
 * states. Mock mode (`video.__mockCaptions`, see ../mockCaptions.js)
 * synthesizes native TextTracks instead, since real masters carry no
 * subtitle renditions until the pipeline ships.
 *
 * Default rule (canonical, card 868kk6pe2): auto-on in the viewer's language
 * when it differs from `video.natural_language` AND a `done` track exists
 * for the viewer's language AND that track is actually playable; off when
 * languages match, no track, or the viewer's language is unknown.
 */
export default function useCaptions({ info, videoRef, hls }) {
    const { user } = useAuth();

    const video = info.video;
    const captions = useMemo(() => video?.captions ?? [], [video]);
    const naturalLanguage = video?.natural_language ?? null;
    const isMock = !!video?.__mockCaptions;

    const [availableLangs, setAvailableLangs] = useState([]);
    const [activeLang, setActiveLang] = useState(null);

    // The default rule runs once per mount, when tracks first appear.
    const defaultRuleAppliedRef = useRef(false);

    const applyLanguage = useCallback((lang) => {
        const canonical = lang ? toCanonicalLang(lang) : null;

        if (isMock) {
            const element = videoRef.current;
            if (element) {
                Array.from(element.textTracks).forEach((track) => {
                    if (!toCanonicalLang(track.language)) return;
                    track.mode =
                        canonical && langsMatch(track.language, canonical) ? "showing" : "hidden";
                });
            }
        } else if (hls) {
            if (canonical === null) {
                hls.subtitleDisplay = false;
                hls.subtitleTrack = -1;
            } else {
                const index = hls.subtitleTracks.findIndex((track) =>
                    langsMatch(track.lang, canonical)
                );
                if (index < 0) return;
                hls.subtitleTrack = index;
                hls.subtitleDisplay = true;
            }
        }

        setActiveLang(canonical);
    }, [hls, isMock, videoRef]);

    // Explicit viewer choice — remembered for the rest of the session.
    const setCaptionLanguage = useCallback((lang) => {
        sessionChoice = lang ? toCanonicalLang(lang) : null;
        applyLanguage(sessionChoice);
    }, [applyLanguage]);

    // Real path: playable languages mirror the master's subtitle renditions.
    useEffect(() => {
        if (!hls || isMock) return;

        const readTracks = () => {
            const langs = [];
            (hls.subtitleTracks ?? []).forEach((track) => {
                const canonical = toCanonicalLang(track.lang);
                if (canonical && !langs.includes(canonical)) langs.push(canonical);
            });
            setAvailableLangs(langs);
        };

        hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, readTracks);
        // The manifest may already be parsed by the time this effect attaches.
        if (hls.subtitleTracks?.length) readTracks();

        return () => {
            hls.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, readTracks);
            setAvailableLangs([]);
        };
    }, [hls, isMock]);

    // Mock path: synthesize one native TextTrack per `done` language, filled
    // with sample cues once the duration is known.
    useEffect(() => {
        if (!isMock) return;
        const element = videoRef.current;
        if (!element) return;

        const doneLangs = captions
            .filter((track) => track.status === "done")
            .map((track) => track.language);
        if (!doneLangs.length) return;

        let cancelled = false;

        const install = () => {
            if (cancelled) return;
            // addTextTrack is irreversible on a media element — install once
            // (also guards StrictMode's double effect run in development).
            if (!element.__mockCaptionTracksInstalled) {
                element.__mockCaptionTracksInstalled = true;
                doneLangs.forEach((lang) => {
                    const track = element.addTextTrack(
                        "subtitles",
                        CAPTION_LANGUAGE_NAMES[lang] ?? lang,
                        lang.replace("_", "-")
                    );
                    track.mode = "hidden";
                    buildMockCues(lang, element.duration).forEach(({ start, end, text }) => {
                        track.addCue(new VTTCue(start, end, text));
                    });
                });
            }
            setAvailableLangs(doneLangs);
        };

        if (element.readyState >= 1) install();
        else element.addEventListener("loadedmetadata", install, { once: true });

        return () => {
            cancelled = true;
            element.removeEventListener("loadedmetadata", install);
        };
    }, [isMock, captions, videoRef]);

    // Auto-enable, gated on the track actually being playable — runs when
    // tracks first appear, once per mount.
    useEffect(() => {
        if (!availableLangs.length || defaultRuleAppliedRef.current) return;
        defaultRuleAppliedRef.current = true;

        let target = null;
        if (sessionChoice !== undefined) {
            target =
                sessionChoice && availableLangs.includes(sessionChoice) ? sessionChoice : null;
        } else {
            const viewer = viewerLanguage(user);
            const viewerTrackDone = captions.some(
                (track) => track.status === "done" && langsMatch(track.language, viewer)
            );
            if (
                viewer &&
                naturalLanguage &&
                !langsMatch(viewer, naturalLanguage) &&
                viewerTrackDone &&
                availableLangs.includes(viewer)
            ) {
                target = viewer;
            }
        }

        if (target) applyLanguage(target);
    }, [availableLangs, captions, naturalLanguage, user, applyLanguage]);

    // 'c' toggles captions, YouTube-style. Lives here rather than in
    // usePlayerControls' shortcut map because the toggle needs caption state;
    // the field/modifier guards mirror that map's.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key?.toLowerCase() !== "c") return;
            const target = event.target;
            if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return;
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (!availableLangs.length) return;

            event.preventDefault();
            if (activeLang) {
                setCaptionLanguage(null);
                return;
            }
            const viewer = viewerLanguage(user);
            const preferred =
                (sessionChoice && availableLangs.includes(sessionChoice) && sessionChoice) ||
                (viewer && availableLangs.includes(viewer) && viewer) ||
                availableLangs[0];
            setCaptionLanguage(preferred);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [availableLangs, activeLang, setCaptionLanguage, user]);

    return { captions, naturalLanguage, availableLangs, activeLang, setCaptionLanguage };
}
